import React, { useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';

/**
 * Structured Link Component
 * Renders various types of links with optional icons and descriptions
 */
const StructuredLink = ({
  href,
  text,
  title,
  description,
  icon,
  target = '_blank',
  rel = 'noopener noreferrer',
  variant = 'default', // 'default', 'button', 'card'
  disabled = false,
  onClick,
  className,
  style = {},
}) => {
  const { theme } = useAppTheme();
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'button':
        return {
          display: 'inline-flex',
          alignItems: 'center',
          padding: `${theme.spacing.sm} ${theme.spacing.md}`,
          backgroundColor: theme.colors.primary,
          color: theme.colors.text.inverse,
          textDecoration: 'none',
          borderRadius: theme.borderRadius.sm,
          fontSize: '14px',
          fontWeight: '500',
          transition: 'background-color 0.2s',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        };

      case 'card':
        return {
          display: 'block',
          padding: theme.spacing.md,
          backgroundColor: theme.colors.background.sidebar,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.md,
          textDecoration: 'none',
          color: 'inherit',
          transition: 'border-color 0.2s, background-color 0.2s',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        };

      default:
        return {
          color: theme.colors.primary,
          textDecoration: 'underline',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'color 0.2s',
        };
    }
  };

  const renderIcon = () => {
    if (!icon) return null;

    // Handle different icon types
    if (typeof icon === 'string') {
      // If it's a URL, render as image
      if (icon.startsWith('http') || icon.startsWith('/')) {
        return (
          <img
            src={icon}
            alt=""
            style={{
              width: '16px',
              height: '16px',
              marginRight: theme.spacing.sm,
              objectFit: 'contain',
            }}
          />
        );
      }
      // Otherwise, treat as emoji or unicode
      return <span style={{ marginRight: theme.spacing.sm, fontSize: '16px' }}>{icon}</span>;
    }

    return icon;
  };

  const styles = {
    container: {
      ...style,
    },
    link: {
      ...getVariantStyles(),
    },
    linkHover: {
      ...(variant === 'button' && {
        backgroundColor: disabled ? theme.colors.primary : theme.colors.accent,
      }),
      ...(variant === 'card' && {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.background.main,
      }),
      ...(variant === 'default' && {
        color: disabled ? theme.colors.primary : theme.colors.accent,
      }),
    },
    content: {
      display: 'flex',
      alignItems: variant === 'card' ? 'flex-start' : 'center',
      flexDirection: variant === 'card' ? 'column' : 'row',
    },
    textContent: {
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      fontSize: variant === 'card' ? '16px' : '14px',
      fontWeight: variant === 'card' ? '600' : '500',
      color: variant === 'button' ? 'inherit' : theme.colors.text.primary,
      margin: 0,
      marginBottom: description && variant === 'card' ? theme.spacing.xs : 0,
    },
    description: {
      fontSize: '12px',
      color: variant === 'button' ? theme.colors.text.inverse : theme.colors.text.muted,
      margin: 0,
      lineHeight: '1.4',
    },
    externalIcon: {
      fontSize: '12px',
      marginLeft: theme.spacing.xs,
      opacity: 0.7,
    },
  };

  // Determine if this is an external link
  const isExternal = href && (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:'));

  // Use provided target, or default to _blank for external links
  const linkTarget = target || (isExternal ? '_blank' : undefined);
  const linkRel = rel || (isExternal && linkTarget === '_blank' ? 'noopener noreferrer' : undefined);

  return (
    <div style={styles.container} className={className}>
      <a
        href={disabled ? undefined : href}
        target={linkTarget}
        rel={linkRel}
        style={styles.link}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (!disabled) {
            Object.assign(e.target.style, styles.linkHover);
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            Object.assign(e.target.style, styles.link);
          }
        }}
      >
        <div style={styles.content}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {renderIcon()}
            <div style={styles.textContent}>
              <span style={styles.title}>
                {title || text || href}
                {isExternal && variant === 'default' && <span style={styles.externalIcon}>↗</span>}
              </span>
              {description && variant !== 'default' && <span style={styles.description}>{description}</span>}
            </div>
          </div>
          {description && variant === 'default' && (
            <div style={{ ...styles.description, marginTop: theme.spacing.xs }}>{description}</div>
          )}
        </div>
      </a>
    </div>
  );
};

export default StructuredLink;
